/**
 * `/` (PRD §4.2) — what each role sees first.
 *
 * Grantonomy and company admins get the live-status widget (D-38). Employees have no separate
 * dashboard — `/` redirects to `/my/tracking` (same project list).
 */

import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Building2 } from "lucide-react";
import { LiveStatusSection } from "@/components/tracking/live-status";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { bundeslandLabel } from "@/lib/format";
import { liveFromState } from "@/lib/live-back";
import { useQueryTab } from "@/lib/query-tab";
import { useSession } from "@/lib/session";
import { useCompanies, useProjects } from "@/lib/tenancy";
import { useLiveStatus } from "@/lib/tracking";
import { DASHBOARD_SHOW_MORE_STEP, showMoreNext, visibleSlice } from "@/lib/visible-slice";

const DASH_VIEWS = ["companies", "projects"] as const;

export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useSession();
  const isGAdmin = user?.role === "grantonomy_admin";
  const isCompanyAdmin = user?.role === "company_admin";
  const isEmployee = user?.role === "employee";
  const isAdmin = isGAdmin || isCompanyAdmin;

  const companies = useCompanies({ enabled: isAdmin });
  const projects = useProjects();
  const liveStatus = useLiveStatus(isAdmin);

  const loading =
    projects.isLoading || (isAdmin && companies.isLoading) || (isAdmin && liveStatus.isLoading);

  const peopleCount = liveStatus.data?.peopleCount ?? 0;
  const exportableTimesheets = liveStatus.data?.exportableTimesheets ?? 0;

  const [dashView, setDashView] = useQueryTab("dash", DASH_VIEWS, "companies");
  const [companyVisible, setCompanyVisible] = useState(DASHBOARD_SHOW_MORE_STEP);
  const [projectVisible, setProjectVisible] = useState(DASHBOARD_SHOW_MORE_STEP);

  const companyList = companies.data ?? [];
  const projectList = projects.data ?? [];
  const shownCompanies = visibleSlice(companyList, companyVisible);
  const shownProjects = visibleSlice(projectList, projectVisible);
  const companyRemaining = Math.max(0, companyList.length - shownCompanies.length);
  const projectRemaining = Math.max(0, projectList.length - shownProjects.length);

  if (isEmployee) {
    return <Navigate to="/my/tracking" replace />;
  }

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
            {t("dashboard.kicker")}
          </p>
          <h1 className="mt-2 text-3xl md:text-5xl">{t("dashboard.title")}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-header-actions">
          {isGAdmin && (
            <Button asChild variant="outline">
              <Link to="/projects">
                <Building2 className="mr-1 size-4" /> {t("nav.companies")}
              </Link>
            </Button>
          )}
        </div>
      </div>

      <div className="mt-10 grid gap-4 md:grid-cols-3" data-testid="dashboard-kpi-strip">
        {[
          {
            key: "projects",
            label: t("dashboard.stats.projects"),
            value: (projects.data ?? []).length,
          },
          { key: "employees", label: t("dashboard.stats.employees"), value: peopleCount },
          {
            key: "timesheets",
            label: t("dashboard.stats.timesheets"),
            value: exportableTimesheets,
          },
        ].map((stat) => (
          <div
            key={stat.key}
            className="rounded-2xl border border-border bg-card p-5"
            data-testid={`dashboard-kpi-${stat.key}`}
          >
            <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
              {stat.label}
            </div>
            <div className="mt-2 font-display text-3xl font-extrabold tabular-nums">
              {loading ? "…" : stat.value}
            </div>
          </div>
        ))}
      </div>

      {isAdmin && <LiveStatusSection />}

      {isGAdmin && (
        <div className="mt-12" data-testid="dashboard-companies-section">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-2xl">
              {dashView === "companies" ? t("nav.companies") : t("nav.projects")}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <Tabs
                value={dashView}
                onValueChange={(next) => {
                  if ((DASH_VIEWS as readonly string[]).includes(next)) {
                    setDashView(next as (typeof DASH_VIEWS)[number]);
                  }
                }}
              >
                <TabsList
                  data-testid="dashboard-view-toggle"
                  aria-label={t("dashboard.viewsLabel")}
                >
                  <TabsTrigger value="companies" data-testid="dashboard-view-companies">
                    {t("dashboard.viewCompanies")}
                  </TabsTrigger>
                  <TabsTrigger value="projects" data-testid="dashboard-view-projects">
                    {t("dashboard.viewProjects")}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Button asChild variant="ghost" size="sm">
                <Link to="/projects" data-testid="dashboard-open-list">
                  {dashView === "projects"
                    ? t("dashboard.openAllProjects")
                    : t("dashboard.openAllCompanies")}{" "}
                  <ArrowUpRight className="ml-1 size-4" />
                </Link>
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="rounded-2xl border border-border bg-card p-8 text-sm text-muted-foreground">
              {t("common.loading")}
            </div>
          ) : dashView === "companies" ? (
            companyList.length === 0 ? (
              <div className="grid place-items-center rounded-2xl border border-dashed border-border bg-surface p-16 text-center">
                <Building2 className="size-8 text-muted-foreground" />
                <div className="mt-4 text-lg">{t("companies.emptyTitle")}</div>
                <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                  {t("companies.emptyBody")}
                </p>
              </div>
            ) : (
              <>
                <ul className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {shownCompanies.map((company) => (
                    <li key={company.id}>
                      <Link
                        to={`/companies/${company.id}`}
                        state={liveFromState("/")}
                        data-testid="dashboard-company-card"
                        className="group flex h-full flex-col rounded-2xl border border-border bg-card p-5 transition hover:border-foreground/40"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-lg font-medium">{company.name}</div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              {bundeslandLabel(company.bundesland)}
                            </div>
                          </div>
                          <Building2 className="size-5 shrink-0 text-muted-foreground group-hover:text-foreground" />
                        </div>
                        <div className="mt-4 text-xs text-muted-foreground">
                          {company.projectCount}{" "}
                          {t("dashboardExtra.projects", { count: company.projectCount })}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
                {companyRemaining > 0 && (
                  <div className="mt-4 flex justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="dashboard-show-more"
                      onClick={() => setCompanyVisible((count) => count + DASHBOARD_SHOW_MORE_STEP)}
                    >
                      {t("dashboard.showMorePage", {
                        next: showMoreNext(companyRemaining, DASHBOARD_SHOW_MORE_STEP),
                        shown: shownCompanies.length,
                        total: companyList.length,
                      })}
                    </Button>
                  </div>
                )}
              </>
            )
          ) : projectList.length === 0 ? (
            <div className="grid place-items-center rounded-2xl border border-dashed border-border bg-surface p-16 text-center">
              <Building2 className="size-8 text-muted-foreground" />
              <div className="mt-4 text-lg">{t("dashboard.emptyTitle")}</div>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                {t("dashboard.emptyBody")}
              </p>
            </div>
          ) : (
            <>
              <ul className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {shownProjects.map((project) => (
                  <li key={project.id}>
                    <Link
                      to={`/projects/${project.id}/live`}
                      state={liveFromState("/")}
                      data-testid="dashboard-project-card"
                      className="group flex h-full flex-col justify-between rounded-2xl border border-border bg-card p-5 transition hover:border-foreground/40 hover:bg-surface"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-lg font-medium">{project.name}</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {project.companyName} · {project.startDate.slice(0, 4)}
                        </div>
                      </div>
                      <ArrowUpRight className="mt-6 size-5 text-muted-foreground" />
                    </Link>
                  </li>
                ))}
              </ul>
              {projectRemaining > 0 && (
                <div className="mt-4 flex justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="dashboard-show-more"
                    onClick={() => setProjectVisible((count) => count + DASHBOARD_SHOW_MORE_STEP)}
                  >
                    {t("dashboard.showMorePage", {
                      next: showMoreNext(projectRemaining, DASHBOARD_SHOW_MORE_STEP),
                      shown: shownProjects.length,
                      total: projectList.length,
                    })}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {isCompanyAdmin && !loading && (projects.data ?? []).length === 0 && (
        <div className="mt-12 grid place-items-center rounded-2xl border border-dashed border-border bg-surface p-16 text-center">
          <Building2 className="size-8 text-muted-foreground" />
          <div className="mt-4 text-lg">{t("dashboard.emptyTitle")}</div>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">{t("dashboard.emptyBody")}</p>
        </div>
      )}
    </div>
  );
}
